/**
 * consolidationService.ts
 *
 * Step 3 — HR consolidation. For every employee with a manager-submitted
 * EPRSubmission, build a draft AppraisalForm containing:
 *   - eprSummary derived from RAW scores (no calibration yet)
 *   - anonymised reviewSummary360 from the aggregator (HR view; identity
 *     preserved for attributed reviewers — see CLAUDE.md §D.1)
 *
 * The appraisal stays in 'draft' until calibration (Step 4) overwrites the
 * eprSummary with calibrated scores and Step 5 finalises notes + dev plan.
 *
 * One AppraisalForm per (cycle, employee), keyed `${cycleId}_${employeeId}`.
 */

import {
  doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type {
  EPRSubmission, EPRItem,
} from '../types/eprFramework';
import type {
  AppraisalForm, EPRSummary, AppraisalStatus,
} from '../types/appraisal';
import {
  listSubmissionsForCycle, submissionId as eprSubmissionId,
} from './eprSubmissionService';
import {
  computeComponentScore, computeRawTotal,
} from './eprScoreEngine';
import {
  summarize360ForReviewee,
} from './review360Aggregator';

const COL = 'appraisalForms';

// ─── ID helper ─────────────────────────────────────────────────────

export function appraisalId(cycleId: string, employeeId: string): string {
  return `${cycleId}_${employeeId}`;
}

// ─── EPR summary builder ───────────────────────────────────────────

/**
 * Build the EPR summary from a submission, optionally overriding with
 * calibration adjustments. For Step 3 (consolidation), pass the submission
 * as-is; for Step 4 (calibration lock), pass the calibrated version.
 */
export function buildEPRSummary(sub: EPRSubmission): EPRSummary {
  const rawOkr        = computeComponentScore(sub.okrItems);
  const rawKpi        = computeComponentScore(sub.kpiItems);
  const rawCore       = computeComponentScore(sub.coreItems);
  const rawLeadership = sub.weights.leadership > 0
    ? computeComponentScore(sub.leadershipItems)
    : undefined;
  const rawTotal = computeRawTotal(
    { rawOkr, rawKpi, rawCore, rawLeadership },
    sub.weights,
  );

  const finalTotal = sub.scores.calibratedTotal ?? rawTotal;
  const calibratedTotal = sub.scores.calibratedTotal;

  const componentBreakdown: EPRSummary['componentBreakdown'] = [
    {
      component: 'okr',
      weight: sub.weights.okr,
      employeeAvg: avgOf(sub.okrItems, 'employeeSelfScore'),
      managerAvg:  avgOf(sub.okrItems, 'managerScore'),
      finalAvg:    rawOkr,
      weightedScore: rawOkr != null ? rawOkr * sub.weights.okr : undefined,
    },
    {
      component: 'kpi',
      weight: sub.weights.kpi,
      employeeAvg: avgOf(sub.kpiItems, 'employeeSelfScore'),
      managerAvg:  avgOf(sub.kpiItems, 'managerScore'),
      finalAvg:    rawKpi,
      weightedScore: rawKpi != null ? rawKpi * sub.weights.kpi : undefined,
    },
    {
      component: 'core',
      weight: sub.weights.core,
      employeeAvg: avgOf(sub.coreItems, 'employeeSelfScore'),
      managerAvg:  avgOf(sub.coreItems, 'managerScore'),
      finalAvg:    rawCore,
      weightedScore: rawCore != null ? rawCore * sub.weights.core : undefined,
    },
  ];
  if (sub.weights.leadership > 0) {
    componentBreakdown.push({
      component: 'leadership',
      weight: sub.weights.leadership,
      employeeAvg: avgOf(sub.leadershipItems, 'employeeSelfScore'),
      managerAvg:  avgOf(sub.leadershipItems, 'managerScore'),
      finalAvg:    rawLeadership,
      weightedScore: rawLeadership != null ? rawLeadership * sub.weights.leadership : undefined,
    });
  }

  return {
    componentScores: { rawOkr, rawKpi, rawCore, rawLeadership, rawTotal, calibratedTotal, finalTotal },
    componentBreakdown,
  };
}

function avgOf(items: EPRItem[], side: 'employeeSelfScore' | 'managerScore'): number | undefined {
  const samples = items
    .map(it => it[side])
    .filter((x): x is 1 | 2 | 3 | 4 | 5 => typeof x === 'number');
  if (samples.length === 0) return undefined;
  return samples.reduce((a, b) => a + b, 0) / samples.length;
}

// ─── Per-employee consolidation ────────────────────────────────────

export interface ConsolidateOneInput {
  cycleId: string;
  employeeId: string;
  supervisorId: string;
}

/**
 * Idempotent — re-runs overwrite the draft summaries but never touch
 * signatures, manager notes, or development plan (those belong to Step 5).
 */
export async function consolidateOne(input: ConsolidateOneInput): Promise<AppraisalForm> {
  const sub = await getDoc(doc(db, 'eprSubmissions', eprSubmissionId(input.cycleId, input.employeeId)));
  if (!sub.exists()) {
    throw new Error(`No EPRSubmission for ${input.cycleId}/${input.employeeId}`);
  }
  const eprSub = sub.data() as EPRSubmission;
  if (eprSub.status !== 'manager_submitted' && eprSub.status !== 'locked') {
    throw new Error(`EPRSubmission not ready for consolidation (status: ${eprSub.status})`);
  }

  const eprSummary = buildEPRSummary(eprSub);
  // HR is the requester at consolidation — full audit. The summary written to
  // the appraisal doc is for HR's eyes; employee/manager views must run the
  // aggregator with their own role at READ time, not from this stored value.
  const review360 = await summarize360ForReviewee(
    input.cycleId, input.employeeId, 'hr',
  );

  const id = appraisalId(input.cycleId, input.employeeId);
  const ref = doc(db, COL, id);
  const existing = await getDoc(ref);

  if (existing.exists()) {
    // Update summaries only, leave the rest
    await updateDoc(ref, {
      eprSummary,
      reviewSummary360: review360,
      updatedAt: serverTimestamp(),
    });
  } else {
    const data: Omit<AppraisalForm, 'id'> = {
      cycleId:        input.cycleId,
      employeeId:     input.employeeId,
      supervisorId:   input.supervisorId,
      eprSummary,
      reviewSummary360: review360,
      managerCombinedNotes: '',
      developmentPlan: [],
      status: 'draft' as AppraisalStatus,
      createdAt: serverTimestamp() as unknown as Timestamp,
    };
    await setDoc(ref, data);
  }

  const fresh = await getDoc(ref);
  return { id, ...(fresh.data() as Omit<AppraisalForm, 'id'>) };
}

// ─── Cycle-level consolidation ─────────────────────────────────────

export interface ConsolidateCycleResult {
  cycleId: string;
  appraisals: AppraisalForm[];
  skipped: Array<{ employeeId: string; reason: string }>;
}

/**
 * Walk every submission in the cycle. For ones in manager_submitted or
 * locked state, run consolidateOne. Submissions still in draft or
 * employee_submitted are skipped with a reason.
 */
export async function consolidateCycle(cycleId: string): Promise<ConsolidateCycleResult> {
  const subs = await listSubmissionsForCycle(cycleId);
  const appraisals: AppraisalForm[] = [];
  const skipped: ConsolidateCycleResult['skipped'] = [];

  for (const s of subs) {
    if (s.status !== 'manager_submitted' && s.status !== 'locked') {
      skipped.push({ employeeId: s.employeeId, reason: `status=${s.status}` });
      continue;
    }
    try {
      const a = await consolidateOne({
        cycleId,
        employeeId: s.employeeId,
        supervisorId: s.supervisorId,
      });
      appraisals.push(a);
    } catch (e) {
      skipped.push({ employeeId: s.employeeId, reason: (e as Error).message });
    }
  }

  await updateDoc(doc(db, 'reviewCycles', cycleId), {
    currentStep: 3,
    updatedAt: serverTimestamp(),
  });

  return { cycleId, appraisals, skipped };
}
